import React, { FC } from "react"

import "./SocialShare.scss"

export const SOCIAL_SHARE_DIRECTION = {
  DEFAULT: "VERTICAL",
  HORIZONTAL: "HORIZONTAL",
  VERTICAL: "VERTICAL",
}

export type SocialShareProps = {
  author?: {
    twitter?: string
    frontmatter?: {
      twitter?: string
    }
  }
  className?: string
  direction?: string
  emailBody?: string
  title: string
  url: string
}

const SocialShare: FC<SocialShareProps> = ({
  author,
  className,
  emailBody = `Check out this article I found on the Help Scout blog`,
  title,
  url,
  direction = SOCIAL_SHARE_DIRECTION.DEFAULT,
}) => {
  const twitterText = `${title}${author && (author?.twitter || author.frontmatter?.twitter)
    ? " by @" + author?.twitter || author.frontmatter?.twitter
    : ""
    } via @helpscout`

  const emailSubject = title
  const emailBodyWithUrl = `${emailBody}: ${url}`

  return (
    <ul
      className={`${className} SocialShare SocialShare--direction-${direction}`}
    >
      <li>
        <a
          aria-label="Share on Twitter"
          className="twitter"
          href={`//twitter.com/share?url=${encodeURIComponent(
            url as string
          )}&text=${encodeURIComponent(twitterText)}`}
          rel="nofollow"
          target="_blank"
          title="Share on Twitter"
        >
          <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.8037 8.28771L16.5713 1.58337H15.2046L10.1966 7.40466L6.19674 1.58337H1.58337L7.63195 10.3862L1.58337 17.4167H2.95018L8.23874 11.2692L12.4629 17.4167H17.0763L10.8034 8.28771H10.8037ZM8.93171 10.4637L8.31887 9.58718L3.44266 2.61229H5.542L9.47715 8.24124L10.09 9.11781L15.2052 16.4346H13.1059L8.93171 10.4641V10.4637Z" fill="#131B24" />
          </svg>
        </a>
      </li>
      <li>
        <a
          aria-label="Share on Facebook"
          className="facebook"
          href={`//facebook.com/sharer.php?u=${url}`}
          rel="nofollow"
          target="_blank"
          title="Share on Facebook"
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.625 11C20.625 5.6843 16.3157 1.375 11 1.375C5.6843 1.375 1.375 5.6843 1.375 11C1.375 15.5137 4.48272 19.3014 8.67498 20.3416V13.9414H6.69031V11H8.67498V9.73258C8.67498 6.45661 10.1576 4.93818 13.3739 4.93818C13.9838 4.93818 15.036 5.05791 15.4664 5.17726V7.84339C15.2392 7.81952 14.8446 7.80758 14.3545 7.80758C12.7764 7.80758 12.1665 8.40549 12.1665 9.95973V11H15.3105L14.7703 13.9414H12.1665V20.5545C16.9317 19.979 20.625 15.9211 20.625 11Z" fill="#131B24" />
          </svg>


        </a>
      </li>
      <li>
        <a
          aria-label="Share on LinkedIn"
          className="linkedin"
          href={`//www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
            url as string
          )}`}
          rel="nofollow"
          target="_blank"
          title="Share on LinkedIn"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.1447 1.98328H3.65234C3.00602 1.98328 2.4834 2.49353 2.4834 3.12439V16.6724C2.4834 17.3033 3.00602 17.8166 3.65234 17.8166H17.1447C17.791 17.8166 18.3167 17.3033 18.3167 16.6755V3.12439C18.3167 2.49353 17.791 1.98328 17.1447 1.98328ZM7.18083 15.4756H4.83057V7.91768H7.18083V15.4756ZM6.0057 6.8879C5.25114 6.8879 4.64193 6.27869 4.64193 5.52722C4.64193 4.77576 5.25114 4.16654 6.0057 4.16654C6.75716 4.16654 7.36637 4.77576 7.36637 5.52722C7.36637 6.27559 6.75716 6.8879 6.0057 6.8879ZM15.9757 15.4756H13.6286V11.8018C13.6286 10.9266 13.6131 9.79789 12.4071 9.79789C11.1855 9.79789 11 10.7535 11 11.74V15.4756H8.65593V7.91768H10.9072V8.95056H10.9382C11.2505 8.35681 12.0174 7.72905 13.1585 7.72905C15.5366 7.72905 15.9757 9.29382 15.9757 11.3287V15.4756Z" fill="#131B24" />
          </svg>

        </a>
      </li>
      <li>
        <a
          aria-label="Share via Email"
          className="email"
          href={`mailto:?subject=${encodeURIComponent(
            emailSubject as string
          )}&body=${encodeURIComponent(emailBodyWithUrl)}`}
          rel="nofollow"
          target="_blank"
          title="Share via Email"
        >
          <svg viewBox="0 0 16 13" xmlns="http://www.w3.org/2000/svg" className={className}>
            <path d="M13 0H3C1.3 0 0 1.3 0 3v7c0 1.7 1.3 3 3 3h10c1.7 0 3-1.3 3-3V3c0-1.7-1.3-3-3-3zm0 2c.2 0 .3.1.5.1L8 5.8 2.6 2.2c.1-.1.2-.2.4-.2h10zm0 9H3c-.6 0-1-.4-1-1V4.2l5.4 3.6c.2.1.4.2.6.2.2 0 .4-.1.6-.2L14 4.2V10c0 .6-.4 1-1 1z" fillRule="nonzero" fill="#8d98a1" />
          </svg>
        </a>
      </li>
    </ul>
  )
}

export default SocialShare
